import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { products } from '../data/products';
import ProductCard from './ProductCard';
import { ProductListStructuredData } from './SEO/StructuredData';

// Define categories with subcategories
const CATEGORIES = {
  Popular: ['All'],
  Development: [
    'Python Development',
    'Web Development',
    'DevOps Tools',
    'System Administration',
    'IoT Security'
  ],
  Cybersecurity: [
    'Cybersecurity Training',
    'Cybersecurity Tools',
    'Cybersecurity Certification',
    'Network Security'
  ],
  Business: [
    'Business Planning',
    'Business Analytics',
    'Business Operations',
    'Business Strategy',
    'Business Presentations',
    'Business Documents'
  ],
  'Legal & HR': [
    'Legal Documents',
    'HR Documents'
  ],
  Marketing: [
    'Marketing Strategy',
    'Digital Marketing',
    'Digital Media'
  ],
  Education: [
    'Educational Resources',
    'AI & Machine Learning',
    'Data Science',
    'Career Resources'
  ],
  Financial: [
    'Financial Management'
  ]
};

const SORT_OPTIONS = [
  { value: 'a-z', label: 'Name: A to Z' },
  { value: 'z-a', label: 'Name: Z to A' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' }
];

// Get min and max prices from products
const prices = products.map(product => product.price);
const MIN_PRICE = Math.min(...prices);
const MAX_PRICE = Math.max(...prices);

const ProductList = () => {
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  const searchFromUrl = searchParams.get('search');
  
  const [selectedMainCategory, setSelectedMainCategory] = useState('Popular');
  const [selectedSubCategory, setSelectedSubCategory] = useState(categoryFromUrl || 'All');
  const [searchQuery, setSearchQuery] = useState(searchFromUrl || '');
  const [isLoading, setIsLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('a-z');
  const [priceRange, setPriceRange] = useState({ min: MIN_PRICE, max: MAX_PRICE });

  useEffect(() => {
    if (categoryFromUrl) {
      const mainCat = Object.entries(CATEGORIES).find(([_, subs]) => 
        subs.includes(categoryFromUrl)
      )?.[0] || 'Popular';
      setSelectedMainCategory(mainCat);
      setSelectedSubCategory(categoryFromUrl);
    }
    if (searchFromUrl) {
      setSearchQuery(decodeURIComponent(searchFromUrl));
    }
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [categoryFromUrl, searchFromUrl]);

  const handlePriceChange = (type) => (e) => {
    const value = parseInt(e.target.value);
    setPriceRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedSubCategory === 'All' || product.category === selectedSubCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = product.price >= priceRange.min && product.price <= priceRange.max;
    return matchesCategory && matchesSearch && matchesPrice;
  }).sort((a, b) => {
    switch (sortOrder) {
      case 'a-z': return a.name.localeCompare(b.name);
      case 'z-a': return b.name.localeCompare(a.name);
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      default: return 0;
    }
  });

  return (
    <>
      <Helmet>
        <title>Pingfence | {selectedSubCategory === 'All' ? 'All Digital Assets' : selectedSubCategory}</title>
        <meta name="description" content={`Browse our collection of ${selectedSubCategory.toLowerCase()} digital assets. Find high-quality resources for your projects.`} />
        <link rel="canonical" href={`https://pingfence.vercel.app/products${categoryFromUrl ? `?category=${categoryFromUrl}` : ''}`} />
      </Helmet>
      <ProductListStructuredData products={filteredProducts} category={selectedSubCategory} />
      
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Left Sidebar (Filters & Categories) */}
          <div className="w-full lg:w-64 flex-shrink-0 space-y-8">
            
            {/* Categories Navigation */}
            <div className="bg-secondary/30 p-6 rounded-xl border border-primary/10">
              <h3 className="text-lg font-bold text-black mb-4">Categories</h3>
              <div className="space-y-6">
                {/* Main Categories */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Departments</p>
                  <div className="flex flex-col gap-1">
                    {Object.keys(CATEGORIES).map((mainCategory) => (
                      <button
                        key={mainCategory}
                        onClick={() => {
                          setSelectedMainCategory(mainCategory);
                          setSelectedSubCategory(CATEGORIES[mainCategory][0]);
                        }}
                        className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                          selectedMainCategory === mainCategory
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-gray-800 hover:bg-secondary/80 hover:text-black'
                        }`}
                      >
                        {mainCategory}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subcategories */}
                {CATEGORIES[selectedMainCategory].length > 1 && (
                  <div className="space-y-2 pt-4 border-t border-primary/10">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">{selectedMainCategory}</p>
                    <div className="flex flex-col gap-1">
                      {CATEGORIES[selectedMainCategory].map((subCategory) => (
                        <button
                          key={subCategory}
                          onClick={() => setSelectedSubCategory(subCategory)}
                          className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                            selectedSubCategory === subCategory
                              ? 'bg-primary text-black shadow-md shadow-primary/20'
                              : 'text-gray-800 hover:bg-secondary/80 hover:text-black'
                          }`}
                        >
                          {subCategory}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Price Filter Panel */}
            <div className="bg-secondary/30 p-6 rounded-xl border border-primary/10">
              <h3 className="text-lg font-bold text-black mb-4">Price</h3>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm text-gray-800">Min: ${priceRange.min}</label>
                  <input
                    type="range"
                    min={MIN_PRICE}
                    max={MAX_PRICE}
                    value={priceRange.min}
                    onChange={handlePriceChange('min')}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-slider"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm text-gray-800">Max: ${priceRange.max}</label>
                  <input
                    type="range"
                    min={MIN_PRICE}
                    max={MAX_PRICE}
                    value={priceRange.max}
                    onChange={handlePriceChange('max')}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-slider"
                  />
                </div>
                
                <button
                  onClick={() => setPriceRange({ min: MIN_PRICE, max: MAX_PRICE })}
                  className="text-sm text-primary hover:text-primary-light transition-colors"
                >
                  Reset Price Range
                </button>
              </div>
            </div>

          </div>

          {/* Right Main Content */}
          <div className="flex-1 space-y-6 min-w-0">
            
            {/* Search and Sort Header */}
            <div className="bg-secondary/30 p-4 rounded-xl border border-primary/10 flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-stretch flex-1 w-full max-w-md border-4 border-black shadow-[4px_4px_0px_0px_#000000] bg-white h-12">
                <input
                  type="text"
                  placeholder="Search digital assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-full px-4 font-bold text-black placeholder-gray-500 focus:outline-none bg-transparent rounded-none"
                />
                <div className="bg-primary px-5 flex items-center justify-center border-l-4 border-black">
                  <MagnifyingGlassIcon className="h-6 w-6 font-black text-black" />
                </div>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <span className="text-gray-800 text-sm whitespace-nowrap hidden sm:inline">Sort by:</span>
                <div className="relative flex-1 sm:w-48">
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="appearance-none w-full h-11 pl-4 pr-10 bg-secondary border border-primary/20 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-300 cursor-pointer text-sm"
                  >
                    {SORT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-800 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 transition-opacity duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
              {isLoading ? (
                Array.from({ length: 9 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-secondary/50 rounded-xl h-[300px]"></div>
                  </div>
                ))
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))
              ) : (
                <div className="col-span-full text-center py-16 bg-secondary/30 rounded-xl border border-primary/10">
                  <p className="text-gray-800 text-lg">No products found matching your criteria.</p>
                  <button
                    onClick={() => {
                      setSelectedMainCategory('Popular');
                      setSelectedSubCategory('All');
                      setPriceRange({ min: MIN_PRICE, max: MAX_PRICE });
                      setSearchQuery('');
                    }}
                    className="mt-4 inline-flex items-center gap-2 btn btn-primary px-6 py-2"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductList;